import { memo, useEffect, useState, useCallback } from "react";
import Form from "../Form.js";
import { Typography, Box } from "@mui/material";
import {
    getOwners,
    getOwner,
    getAnimal,
    checkIfAnimalExists,
    getLastAnimalLambing,
    getAnimalByFlockCode,
} from "../../api/index.js";
import Spinner from "../Spinner.js";
import { useSnackbar } from "../../utils/index.js";
import AnimalForm from "../forms/AnimalForm.js";
import LambingForm from "../forms/LambingForm.js";
import useSelectStyles from "../styles/Select.js";
import WeaningForm from "../forms/WeaningForm.js";
import Pedigree from "../pdf/Pedigree.js";
import AnimalTransferForm from "../forms/AnimalTransferForm.js";
import SelectAnimalPopup from "../popups/SelectAnimalPopup.js";

const OwnerAnimalSelect = ({
    type,
}) => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [ownerId, setOwnerId] = useState();
    const [fetchedOwner, setFetchedOwner] = useState();
    const [lastSelectedOwner, setLastSelectedOwner] = useState(null);
    const [isAnimalSelected, setIsAnimalSelected] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedAnimal, setFetchedAnimal] = useState(null);
    const [lactationNumber, setLactationNumber] = useState(null);
    const [isSelectAnimalPopupOpen, setIsAnimalPopupOpen] = useState(false);
    const [animalsForPopup, setAnimalsForPopup] = useState([]);
    const [ownerForPopup, setOwnerForPopup] = useState(null);
    const [externalSaleAnimal, setExternalSaleAnimal] = useState(null);

    const [flockCode, setFlockCode] = useState(null);

    const classes = useSelectStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);
                const ownersDropdownArr = []
                for (var owner of owners) {
                    ownersDropdownArr.push({ value: `${owner.code} - ${owner.lastName} ${owner.firstName}`, text: `${owner.code} - ${owner.lastName} ${owner.firstName}` });
                }
                setOwnersDropdownValues(ownersDropdownArr);
            } catch (err) {
                error(err)
            }
        }
    )
    useEffect(() => {
        fetchData();
    }, []);

    const selectFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
            defaultValue: lastSelectedOwner,
        },
        {
            customType: "input",
            id: "animal",
            type: "text",
            placeholder: "Ζώο",
            required: true,
            helperText: type === "CreateAnimal" ? "12-ψήφιος Κωδικός Γεν. Βιβλίου" : "4-ψήφιος Κωδικός"
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή"
        },
    ]

    const animalSubmit = async (values) => {
        setIsLoading(true);
        const selectedOwnerId = fetchedOwners.find((owner) =>
            `${owner.code} - ${owner.lastName} ${owner.firstName}` === values?.owner)._id

        setLastSelectedOwner(values?.owner);
        setOwnerForPopup(values?.owner);
        const { success: scs, animals, message } = await getAnimalByFlockCode(values?.animal, selectedOwnerId);
        if (scs) {
            if (animals.length === 1) {
                setFetchedAnimal(animals[0]);
                handleOwnerSelectSubmit({ animal: animals[0].registrationCode, owner: values?.owner });
            } else {
                const animalsOptions = animals.map((animal) => ({
                    value: animal?.registrationCode,
                    text: animal?.registrationCode,
                }));
                setAnimalsForPopup(animalsOptions);
                setIsAnimalPopupOpen(true);
            }
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleOwnerCreateSubmit = async (values) => {
        setIsLoading(true);
        const selectedOwnerId = fetchedOwners.find((owner) => `${owner.code} - ${owner.lastName} ${owner.firstName}` === values.owner)._id;

        setLastSelectedOwner(values.owner);
        const { success: successCode, owner, message } = await getOwner(selectedOwnerId);
        if (successCode) {
            const { success: scs, externalSaleAnimal: foundExternalSaleAnimal, message: msg } = await checkIfAnimalExists(values.animal, selectedOwnerId);
            if (scs) {
                setOwnerId(selectedOwnerId);
                setFetchedOwner(owner);
                setIsAnimalSelected(true);
                setFlockCode(values.animal);
                setExternalSaleAnimal(foundExternalSaleAnimal || null);
            } else {
                error(msg);
            }
        } else {
            error(message)
        }

        setIsLoading(false);
    }

    const handleOwnerSelectSubmit = async (values) => {
        setIsLoading(true);
        setIsAnimalPopupOpen(false);
        const selectedOwnerId = fetchedOwners.find((owner) => `${owner.code} - ${owner.lastName} ${owner.firstName}` === values.owner)._id;

        setLastSelectedOwner(values.owner);
        const { success: successCode, owner, message } = await getOwner(selectedOwnerId);

        if (type === "CreateLambing") {
            const { success: scs, lambing, message } = await getLastAnimalLambing(selectedOwnerId, values.animal);
            if (scs) {
                if (lambing?.lactationNumber) {
                    setLactationNumber(lambing.lactationNumber + 1);
                } else {
                    setLactationNumber(1);
                }
            } else {
                error(message);
                setIsLoading(false);
                return;
            }
        }

        if (successCode) {
            const { success: scs, animal, message: msg } = await getAnimal(values.animal, selectedOwnerId);
            if (scs) {
                const isLambingFlow = type === "CreateLambing" || type === "SelectLambing";
                const isFemale = animal.sex === "female" || animal.sex === "Θήλυ";
                if (isLambingFlow && !isFemale) {
                    error("Το επιλεγμένο ζώο πρέπει να είναι θηλυκό");
                    setIsLoading(false);
                    return;
                }

                setOwnerId(selectedOwnerId);
                setFetchedOwner(owner);
                setIsAnimalSelected(true);
                setFlockCode(values.animal);
                setFetchedAnimal(animal);
            } else {
                error(msg);
            }
        } else {
            error(message)
        }

        setIsLoading(false);
    }

    const renderComponentByType = () => {
        switch (type) {
            case "CreateAnimal":
                return (
                    <>
                        <Box className={classes.outerBox}>
                            <Box container flexDirection="column" className={classes.innerBox}>
                                <Typography variant="h5" sx={{ margin: "1rem" }}>
                                    Προσθήκη Ζώου
                                </Typography>
                                <AnimalForm
                                    isCreate={true}
                                    fetchedOwner={fetchedOwner}
                                    setFetchedOwner={setFetchedOwner}
                                    ownerId={ownerId}
                                    setIsAnimalSelected={setIsAnimalSelected}
                                    flockCode={flockCode}
                                    initialExternalSaleAnimal={externalSaleAnimal}
                                />
                            </Box>
                        </Box>
                    </>
                )
            case "SelectAnimal":
                return (
                    <>
                        <Box className={classes.outerBox}>
                            <Box container flexDirection="column" className={classes.innerBox}>
                                <Typography variant="h5" sx={{ margin: "1rem" }}>
                                    Επεξεργασία Ζώου
                                </Typography>
                                <AnimalForm
                                    isCreate={false}
                                    fetchedOwner={fetchedOwner}
                                    setFetchedOwner={setFetchedOwner}
                                    ownerId={ownerId}
                                    setIsAnimalSelected={setIsAnimalSelected}
                                    flockCode={flockCode}
                                    fetchedAnimal={fetchedAnimal}
                                    setFetchedAnimal={setFetchedAnimal}
                                    ownersDropdownValues={ownersDropdownValues}
                                    fetchedOwners={fetchedOwners}
                                />
                            </Box>
                        </Box>
                    </>
                )
            case "CreateLambing":
                return (
                    <>
                        <Box className={classes.outerBox}>
                            <Box container flexDirection="column" className={classes.innerBox}>
                                <LambingForm
                                    selectedOwner={fetchedOwner}
                                    selectedAnimal={fetchedAnimal}
                                    setIsAnimalSelected={setIsAnimalSelected}
                                    isCreate={true}
                                    lactationNumber={lactationNumber}
                                />
                            </Box>
                        </Box>
                    </>
                )
            case "SelectLambing":
                return (
                    <>
                        <Box className={classes.outerBox}>
                            <Box container flexDirection="column" className={classes.innerBox}>
                                <LambingForm
                                    selectedOwner={fetchedOwner}
                                    selectedAnimal={fetchedAnimal}
                                    setIsAnimalSelected={setIsAnimalSelected}
                                    isCreate={false}
                                />
                            </Box>
                        </Box>
                    </>
                )
            case "EndLambing":
                return (
                    <>
                        <Box className={classes.outerBox}>
                            <Box container flexDirection="column" className={classes.innerBox}>
                                <WeaningForm
                                    selectedOwner={fetchedOwner}
                                    selectedAnimal={fetchedAnimal}
                                    setIsAnimalSelected={setIsAnimalSelected}
                                />
                            </Box>
                        </Box>
                    </>
                )
            case "Pedigree":
                return (
                    <>
                        <Pedigree
                            selectedOwner={fetchedOwner}
                            selectedAnimal={fetchedAnimal}
                        />
                    </>
                )
            case "TransferAnimal":
                return (
                    <>
                        <AnimalTransferForm
                            selectedOwner={fetchedOwner}
                            selectedAnimal={fetchedAnimal}
                            owners={fetchedOwners}
                            ownersDropdownValues={ownersDropdownValues}
                            setIsAnimalSelected={setIsAnimalSelected}
                        />
                    </>
                )
        }
    }

    return (
        <>
            <Spinner open={isLoading} />
            <SelectAnimalPopup
                isOpen={isSelectAnimalPopupOpen}
                onClose={() => setIsAnimalPopupOpen(false)}
                onDecline={() => setIsAnimalPopupOpen(false)}
                animals={animalsForPopup}
                handleAnimalSelectFormSubmit={handleOwnerSelectSubmit}
                type="OwnerAnimalSelect"
                selectedOwner={ownerForPopup}

            />
            {!isAnimalSelected && (
                <>

                    <Box className={classes.outerBox}>
                        <Box container flexDirection="column" className={classes.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {type === "CreateAnimal" ? "Επιλέξτε εκτροφή και εισάγετε τον Κωδικό Γενεαλογικού Βιβλίου για το Ζώο"
                                    : "Επιλέξτε την Εκτροφή και το Ζώο που σας ενδιαφέρει"}
                            </Typography>
                            <Form
                                content={selectFormContent}
                                onSubmit={type === "CreateAnimal" ? handleOwnerCreateSubmit : animalSubmit}
                                toResetForm={false} />
                        </Box>
                    </Box>
                </>
            )}
            {isAnimalSelected && renderComponentByType()}
        </>
    )
}


export default memo(OwnerAnimalSelect)
